
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import BidsTabs from '@/modules/BidsModule/components/BidsTabs/index.vue'
import BidsChangeStatusForm from '@/modules/BidsModule/components/BidsChangeStatusForm/index.vue'
import BidSignatories from '@/modules/BidsModule/components/BidSignatories/index.vue'

import UpdateSignersModal from '@/modules/ContractsModule/components/UpdateSignersModal/index.vue'

// composable
import { useRoute } from 'vue-router'
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

// types
import { BidT, BidByIdDogovor24 } from '@/core/types/bids.types'

// realisations
import { bids } from '@/core/realisations/bids.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'

// services
import tmsAPI from '@/new-services/tms.service'

// validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

export const DetailsColumns = [
    {
        name: 'Имя',
        key: 'name',
    },
    {
        name: 'Должность',
        key: 'role',
    },
    {
        name: 'Запрашиваемая стоимость',
        key: 'costOfTransportation',
    },
    {
        name: 'Ответ',
        key: 'status',
    },
    {
        name: 'Комментарий',
        key: 'comment',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        Loader,
        BidsTabs,
        Cheap,
        BidsChangeStatusForm,
        Modal,
        TheInput,
        Checkbox,
        TheTable,
        TableScrollWrapper,
        BidSignatories,
        TheSelect,
        UpdateSignersModal,
    },
    setup() {
        const account = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            bid: null as BidT | null,
            currentTab: 'general',
            contractId: null as number | null,
            //
            is_status_changing: false,
            is_accept_status: false,
            is_decline_status: false,
        })

        const bid_state = reactive({
            is_loading: false,
            is_failed: false,
            is_actions_loading: false,
            bid: null as BidByIdDogovor24 | null,
        })

        const state_cost = reactive({
            is_open: false,
            is_loading: false,
            cost: null as number | null,
            isNds: null as number | null,
        })

        const EditCostValidation = {
            cost: { required, integer },
        }

        const cost_form$ = useVuelidate(EditCostValidation, state_cost)

        const route = useRoute()
        const id = Number(route.params.id)

        const loadBid = async () => {
            try {
                state.is_loading = true

                const response = await bids.getBidNew(id)
                state.bid = response.data

                if (state.bid && state.bid.carrier) {
                    const responseContract = await carriers.getContractInformation(
                        state.bid.carrier.bin
                    )
                    state.contractId = responseContract.data.id
                }

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({ error, title: 'При получении заявки произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const loadBidDogovor24 = async () => {
            try {
                bid_state.is_loading = true

                const response = await bids.getDogovor24ById(id)
                bid_state.bid = response.data

                bid_state.is_failed = false
            } catch (error) {
                console.error(error)
                bid_state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении подписантов произошла ошибка',
                })
            } finally {
                bid_state.is_loading = false
            }
        }

        const onAccepted = () => {
            state.is_accept_status = true
            state.is_status_changing = true
        }

        const onDeclined = () => {
            state.is_decline_status = true
            state.is_status_changing = true
        }

        const onCancelStatusChange = () => {
            state.is_status_changing = false
            state.is_accept_status = false
            state.is_decline_status = false
        }

        const onStatusChanged = () => {
            onCancelStatusChange()
            loadBid()
        }

        const toggleCost = () => (state_cost.is_open = !state_cost.is_open)

        const editCost = async () => {
            cost_form$.value.$touch()
            if (cost_form$.value.$invalid || !state.bid) return

            try {
                state_cost.is_loading = true

                const isNds = state_cost.isNds ? 1 : 0

                await tmsAPI.editBidCost(state.bid.id, state_cost.cost as number, isNds)

                toggleCost()
                loadBid()
                notifications.notify({
                    title: 'Ставка успешно отредактирована',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                state_cost.is_loading = false
            }
        }

        //

        const update_signers_state = reactive({
            is_opened: false,
            signers: [] as {
                email: string
                isSigned: boolean
                isLawyer: boolean
                typeId: number
            }[],
        })

        const onBidUpdateSigners = (bid: BidByIdDogovor24) => {
            update_signers_state.signers = bid.signers.map((signer) => {
                return {
                    email: signer.email,
                    isSigned: signer.isSigned,
                    isLawyer: signer.isLawyer,
                    typeId: signer.typeId,
                }
            })

            update_signers_state.is_opened = true
        }

        const cancelUpdateSigners = () => {
            update_signers_state.is_opened = false
            update_signers_state.signers = []
        }

        const updateSigners = async (signersData: { email: string; newEmail: string | null; dogovor24TypeId: number;}[]) => {
            try {
                bid_state.is_actions_loading = true

                await bids.editSigners((bid_state.bid as BidByIdDogovor24).id, {
                    signersData,
                })

                notifications.notify({
                    title: 'Редактирование успешно завершено!',
                    type: 'success',
                })

                loadBidDogovor24()
                cancelUpdateSigners()
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                bid_state.is_actions_loading = false
            }
        }

        const onCheckBid = async (bid: BidByIdDogovor24) => {
            try {
                bid_state.is_actions_loading = true
                await contracts.checkIsSigned(bid.id)

                loadBidDogovor24()

                notifications.notify({
                    title: 'Заявка успешно проверена!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке заявки произошла ошибка',
                })
            } finally {
                bid_state.is_actions_loading = false
            }
        }

        const onDeleteBid = async (bid: BidByIdDogovor24) => {
            const answer = confirm('Вы уверены, что хотите удалить заявку?')
            if (!answer) return

            try {
                bid_state.is_actions_loading = true

                await contracts.deleteContract(bid.id)

                loadBid()
                loadBidDogovor24()

                notifications.notify({
                    title: 'Заявка удалена',
                    type: 'success',
                })
            } catch (error: any) {
                if (error.length) {
                    notifications.notifyError({
                        error,
                        title: 'При удалении заявки произошла ошибка',
                    })
                } else {
                    notifications.notify({
                        title: 'При удалении заявки произошла ошибка',
                        type: 'danger',
                    })
                }
            } finally {
                bid_state.is_actions_loading = false
            }
        }

        //
        const isGeneral = computed(() => state.currentTab === 'general')
        const isCargo = computed(() => state.currentTab === 'cargo')
        const isCourier = computed(() => state.currentTab === 'courier')
        const isPayment = computed(() => state.currentTab === 'payment')
        const getVatText = computed(() => {
            if (!state.bid) return ''
            if (state.bid.isNds) return '(с НДС)'
            if (!state.bid.isNds) return '(без НДС)'
            return ''
        })

        const isConfirmedByHeadOfTransport = computed(() => {
            if (state.bid && state.bid.status.id === 15) {
                return state.bid.confirmedDetails.headOfTransport.isConfirmed
            }

            return false
        })

        const headOfTransportConfirmationText = computed(() => {
            if (isConfirmedByHeadOfTransport.value) {
                return 'Подтверждено Руководителем Транспортного Отдела'
            }

            return 'Необходимо подтверждение Руководителя Транспортного отдела'
        })

        const isConfirmShowed = computed(() => {
            return Boolean(state.bid && state.bid.status.id === 15)
        })

        const isCanConfirmOrCancel = computed(() => {
            if (!state.bid) return false

            if (isConfirmShowed.value) {
                if (account.havePermission('all-root-permission')) {
                    return true
                }

                if (account.havePermission('bid-cost-check')) {
                    if (state.bid.confirmedDetails.headOfTransport.isConfirmed) {
                        if (state.bid.confirmedDetails.isNeedSecondConfirmation) {
                            if (
                                state.bid.confirmedDetails.headOfOperation &&
                                state.bid.confirmedDetails.headOfOperation.id ===
                                    account.state.user_id
                            ) {
                                return true
                            }
                        }
                    } else {
                        if (
                            state.bid.confirmedDetails.headOfTransport.id === account.state.user_id
                        ) {
                            return true
                        }
                    }
                }
            }

            return false
        })

        const getCheckOrTimesIcon = (is_check: number | boolean) => {
            return `fas ${is_check ? 'fa-check text-green-500' : 'fa-times'}`
        }

        onBeforeMount(() => {
            loadBid()
            loadBidDogovor24()
        })
        //

        return {
            state,
            bid_state,
            state_cost,
            update_signers_state,
            cost_form$,
            //
            loadBid,
            onAccepted,
            onDeclined,
            onCancelStatusChange,
            onStatusChanged,
            toggleCost,
            editCost,
            getCheckOrTimesIcon,
            //
            onBidUpdateSigners,
            cancelUpdateSigners,
            updateSigners,
            onCheckBid,
            onDeleteBid,
            //
            isGeneral,
            isCargo,
            isCourier,
            isPayment,
            isConfirmedByHeadOfTransport,
            headOfTransportConfirmationText,
            isConfirmShowed,
            isCanConfirmOrCancel,
            //
            getVatText,
            DetailsColumns,
            formatDateWithTimeFromBackend,
            formatDateFromBackend
        }
    },
})
